export type PatientEligibility = 'verified' | 'expires_soon' | 'lapsed' | 'pending'
export type PatientStatus = 'active' | 'on_hold' | 'inactive' | 'pending'

export type PatientEquipmentType = 'Rental' | 'Supply' | 'Purchase'
export type PatientEquipmentStatus = 'Active' | 'Inactive' | 'Returned'

export interface PatientEquipment {
  id: string
  hcpcs: string
  item: string
  type: PatientEquipmentType
  since: string
  status: PatientEquipmentStatus
}

export type PatientClaimStatus = 'Paid' | 'Pending' | 'Denied' | 'Submitted'

export interface PatientClaim {
  id: string
  claimNumber: string
  dos: string
  billed: number
  paid: number | null
  status: PatientClaimStatus
}

export type PatientPriorAuthStatus = 'Active' | 'Renew soon' | 'Expired' | 'Pending'

/** Prior auth status shown in the patients directory grid. */
export type PatientPriorAuthGridStatus = 'active' | 'pending_verification' | 'inactive'

export interface PatientPriorAuth {
  id: string
  description: string
  authNumber: string
  expiresOn: string
  status: PatientPriorAuthStatus
}

export interface PatientRecord {
  id: string
  patientId: string
  /** Backend patients.id UUID when loaded from the API. */
  patientBackendId?: string
  firstName: string
  lastName: string
  dateOfBirth: string
  primaryPayor: string
  memberId: string
  lastVerified: string
  eligibility: PatientEligibility
  status: PatientStatus
  /** When set, overrides derived prior-auth grid status. */
  priorAuthGridStatus?: PatientPriorAuthGridStatus
  openArAmount?: number

  sex?: string
  address?: string
  phone?: string
  email?: string
  emergencyContact?: string
  emergencyPhone?: string

  plan?: string
  groupNumber?: string
  coverageEffective?: string
  coverageEnd?: string
  subscriberRelationship?: string
  coordinationOfBenefits?: string
  secondaryInsurance?: boolean
  aobSigned?: boolean
  hipaaSigned?: boolean
  doctorName?: string
  doctorNpi?: string
  doctorPhone?: string
  doctorFax?: string

  clinicalNotes?: string
  equipment?: PatientEquipment[]
  claims?: PatientClaim[]
  priorAuths?: PatientPriorAuth[]
}

export interface PatientsSummary {
  activePatients: number
  pendingEligibility: number
  expiringSoon: number
  coverageGaps: number
  openPatientAr: number
}

export const PATIENT_AVATAR_PALETTE: { bg: string; text: string }[] = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-orange-100', text: 'text-orange-700' },
]

export function patientInitials(record: Pick<PatientRecord, 'firstName' | 'lastName'>): string {
  const first = record.firstName.charAt(0)
  const last = record.lastName.charAt(0)
  return `${first}${last}`.toUpperCase()
}

export function patientFullName(record: Pick<PatientRecord, 'firstName' | 'lastName'>): string {
  return `${record.firstName} ${record.lastName}`.trim()
}

export function patientAvatarColors(
  record: Pick<PatientRecord, 'id'>
): { bg: string; text: string } {
  let hash = 0
  for (let i = 0; i < record.id.length; i += 1) {
    hash = (hash * 31 + record.id.charCodeAt(i)) >>> 0
  }
  return PATIENT_AVATAR_PALETTE[hash % PATIENT_AVATAR_PALETTE.length]
}

export function parsePatientDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const text = value.trim()
  if (!text) return null

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(text)) {
    const [month, day, year] = text.split('/')
    const usDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`)
    if (!Number.isNaN(usDate.getTime())) {
      return usDate
    }
  }

  if (/^\d{8}$/.test(text)) {
    const year = text.slice(0, 4)
    const month = text.slice(4, 6)
    const day = text.slice(6, 8)
    const compactDate = new Date(`${year}-${month}-${day}`)
    if (!Number.isNaN(compactDate.getTime())) {
      return compactDate
    }
  }

  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

export function formatPatientDate(value: string): string {
  if (!value) return ''
  const date = parsePatientDate(value)
  if (!date) return value
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

export function calculateAge(dateOfBirth: string, asOf: Date = new Date()): number {
  if (!dateOfBirth) return 0
  const dob = parsePatientDate(dateOfBirth)
  if (!dob) return 0
  let age = asOf.getFullYear() - dob.getFullYear()
  const monthDiff = asOf.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dob.getDate())) {
    age -= 1
  }
  return Math.max(0, age)
}

export const ELIGIBILITY_LABEL: Record<PatientEligibility, string> = {
  verified: 'Verified',
  expires_soon: 'Expires soon',
  lapsed: 'Lapsed',
  pending: 'Pending',
}

export const PATIENT_GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
] as const

export const PATIENT_SUBSCRIBER_RELATIONSHIP_OPTIONS = [
  { value: '', label: 'Select relationship' },
  { value: 'Self', label: 'Self' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Child', label: 'Child' },
  { value: 'Other', label: 'Other' },
] as const

export const PATIENT_COB_OPTIONS = [
  { value: '', label: 'Select COB' },
  { value: 'Primary', label: 'Primary' },
  { value: 'Secondary', label: 'Secondary' },
  { value: 'Tertiary', label: 'Tertiary' },
] as const

export const STATUS_LABEL: Record<PatientStatus, string> = {
  active: 'Active',
  on_hold: 'On hold',
  inactive: 'Inactive',
  pending: 'Pending',
}

export const ELIGIBILITY_OPTIONS: { value: PatientEligibility | 'all'; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'verified', label: 'Verified' },
  { value: 'expires_soon', label: 'Expires soon' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'pending', label: 'Pending' },
]

export const PRIOR_AUTH_GRID_LABEL: Record<PatientPriorAuthGridStatus, string> = {
  active: 'Active',
  pending_verification: 'Pending Verification',
  inactive: 'Inactive',
}

export const PRIOR_AUTH_GRID_OPTIONS: {
  value: PatientPriorAuthGridStatus | 'all'
  label: string
}[] = [
  { value: 'all', label: 'All prior auth' },
  { value: 'active', label: 'Active' },
  { value: 'pending_verification', label: 'Pending Verification' },
  { value: 'inactive', label: 'Inactive' },
]

export function resolvePriorAuthGridStatus(
  record: Pick<
    PatientRecord,
    'priorAuthGridStatus' | 'priorAuths' | 'status' | 'eligibility'
  >
): PatientPriorAuthGridStatus {
  if (record.priorAuthGridStatus) return record.priorAuthGridStatus

  if (
    record.priorAuths?.some(
      (pa) => pa.status === 'Active' || pa.status === 'Renew soon'
    )
  ) {
    return 'active'
  }
  if (record.status === 'inactive' || record.eligibility === 'lapsed') {
    return 'inactive'
  }
  if (record.eligibility === 'pending' || record.status === 'pending') {
    return 'pending_verification'
  }
  if (record.eligibility === 'verified' && record.status === 'active') {
    return 'active'
  }
  return 'inactive'
}

export function generatePriorAuthRequestNumber(): string {
  const year = new Date().getFullYear()
  const suffix = String(Math.floor(Math.random() * 900000) + 100000)
  return `PA-${year}-${suffix}`
}

export function resolvePatientApiId(record: PatientRecord): string {
  return record.patientBackendId ?? record.patientId ?? record.id
}

export function patientPriorAuthFieldPatch(
  record: PatientRecord
): {
  patient_id: string
  subscriber_id: string
  provider_name: string
  provider_npi: string
  payor_name_hint: string
} {
  const memberId = record.memberId?.trim()
  const payor = record.primaryPayor?.trim()
  return {
    patient_id: resolvePatientApiId(record),
    subscriber_id: memberId && memberId.toUpperCase() !== 'N/A' ? memberId : '',
    provider_name: record.doctorName?.trim() ?? '',
    provider_npi: record.doctorNpi?.trim() ?? '',
    payor_name_hint: payor && payor.toLowerCase() !== 'unknown payor' ? payor : '',
  }
}

/** Prefill payload for the prior-auth create drawer from a patient row. */
export function priorAuthCreatePrefillFromPatient(
  record: PatientRecord
): {
  request_number: string
  patient_id: string
  subscriber_id: string
  payor_id: string
  provider_name: string
  provider_npi: string
} {
  const fields = patientPriorAuthFieldPatch(record)
  return {
    request_number: generatePriorAuthRequestNumber(),
    patient_id: fields.patient_id,
    subscriber_id: fields.subscriber_id,
    payor_id: fields.payor_name_hint,
    provider_name: fields.provider_name,
    provider_npi: fields.provider_npi,
  }
}

/** Member ID for display under the patient name (from eligibility / insurance data). */
export function patientMemberIdLabel(
  record: Pick<PatientRecord, 'memberId'>
): string | null {
  const id = record.memberId?.trim()
  if (!id || id.toUpperCase() === 'N/A') return null
  return id
}

export function findPatientByPatientId(
  records: PatientRecord[],
  patientId: string
): PatientRecord | undefined {
  return records.find(
    (r) => r.patientId === patientId || r.patientBackendId === patientId
  )
}

/** Placeholder rows for patient detail when API data is not yet available. */
export const patientDetailDummyEquipment: PatientEquipment[] = [
  {
    id: 'eq-demo-1',
    hcpcs: 'E1390',
    item: 'Oxygen concentrator',
    type: 'Rental',
    since: '08/2024',
    status: 'Active',
  },
  {
    id: 'eq-demo-2',
    hcpcs: 'E0431',
    item: 'Portable oxygen system',
    type: 'Rental',
    since: '08/2024',
    status: 'Active',
  },
  {
    id: 'eq-demo-3',
    hcpcs: 'A4615',
    item: 'Cannula, nasal (monthly supply)',
    type: 'Supply',
    since: '08/2024',
    status: 'Active',
  },
]

export const patientDetailDummyClaims: PatientClaim[] = [
  {
    id: 'clm-demo-1',
    claimNumber: 'CLM-9841',
    dos: '2026-05-01',
    billed: 284.4,
    paid: 213.3,
    status: 'Paid',
  },
  {
    id: 'clm-demo-2',
    claimNumber: 'CLM-9612',
    dos: '2026-04-01',
    billed: 284.4,
    paid: 213.3,
    status: 'Paid',
  },
  {
    id: 'clm-demo-3',
    claimNumber: 'CLM-9388',
    dos: '2026-03-01',
    billed: 284.4,
    paid: null,
    status: 'Pending',
  },
]

export const patientDetailDummyPriorAuths: PatientPriorAuth[] = [
  {
    id: 'pa-demo-1',
    description: 'Oxygen equipment (E1390, E0431)',
    authNumber: 'PA-2025-00841',
    expiresOn: '2025-12-31',
    status: 'Renew soon',
  },
  {
    id: 'pa-demo-2',
    description: 'Home health aide — 3×/week',
    authNumber: 'PA-2025-01002',
    expiresOn: '2026-06-30',
    status: 'Active',
  },
]

export function patientSummary(records: PatientRecord[]): PatientsSummary {
  const activePatients = records.filter((r) => r.status === 'active').length
  const pendingEligibility = records.filter((r) => r.eligibility === 'pending').length
  const expiringSoon = records.filter((r) => r.eligibility === 'expires_soon').length
  const coverageGaps = records.filter((r) => r.eligibility === 'lapsed').length
  const openPatientAr = records.reduce((sum, r) => sum + (r.openArAmount ?? 0), 0)
  return {
    activePatients,
    pendingEligibility,
    expiringSoon,
    coverageGaps,
    openPatientAr,
  }
}

export const initialPatients: PatientRecord[] = [
  {
    id: 'pat-0001',
    patientId: 'PT-00291',
    firstName: 'Jose',
    lastName: 'Martinez',
    dateOfBirth: '1968-03-14',
    primaryPayor: 'BlueCross BlueShield TX',
    memberId: 'BCB391847201',
    lastVerified: '2026-05-18',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 0,
    sex: 'Male',
    address: '4821 Oak Creek Dr, San Antonio, TX 78201',
    phone: '(210) 555-0142',
    email: 'j.martinez@email.com',
    emergencyContact: 'Maria Martinez (wife)',
    emergencyPhone: '(210) 555-0198',
    plan: 'PPO Select Plus',
    groupNumber: 'GRP-88421',
    coverageEffective: '2025-01-01',
    coverageEnd: '2025-12-31',
    subscriberRelationship: 'Self',
    coordinationOfBenefits: 'Primary',
    secondaryInsurance: false,
    aobSigned: true,
    hipaaSigned: true,
    doctorName: 'Dr. Sandra Lee',
    doctorNpi: '1234567890',
    doctorPhone: '(210) 555-0133',
    doctorFax: '(210) 555-0177',
    clinicalNotes:
      'Patient uses home oxygen concentrator (E1390) continuously. Physician: Dr. Sandra Lee, NPI 1234567890. CMN on file, expires 12/2025 — renewal due.',
    equipment: [
      {
        id: 'eq-1001',
        hcpcs: 'E1390',
        item: 'Oxygen concentrator',
        type: 'Rental',
        since: '08/2024',
        status: 'Active',
      },
      {
        id: 'eq-1002',
        hcpcs: 'E0431',
        item: 'Portable oxygen system',
        type: 'Rental',
        since: '08/2024',
        status: 'Active',
      },
      {
        id: 'eq-1003',
        hcpcs: 'A4615',
        item: 'Cannula, nasal (monthly supply)',
        type: 'Supply',
        since: '08/2024',
        status: 'Active',
      },
    ],
    claims: [
      {
        id: 'clm-9841',
        claimNumber: 'CLM-9841',
        dos: '2026-05-01',
        billed: 284.4,
        paid: 213.3,
        status: 'Paid',
      },
      {
        id: 'clm-9612',
        claimNumber: 'CLM-9612',
        dos: '2026-04-01',
        billed: 284.4,
        paid: 213.3,
        status: 'Paid',
      },
      {
        id: 'clm-9388',
        claimNumber: 'CLM-9388',
        dos: '2026-03-01',
        billed: 284.4,
        paid: null,
        status: 'Pending',
      },
      {
        id: 'clm-9204',
        claimNumber: 'CLM-9204',
        dos: '2026-02-01',
        billed: 284.4,
        paid: 0,
        status: 'Denied',
      },
      {
        id: 'clm-8990',
        claimNumber: 'CLM-8990',
        dos: '2026-01-01',
        billed: 284.4,
        paid: 213.3,
        status: 'Paid',
      },
    ],
    priorAuths: [
      {
        id: 'pa-0841',
        description: 'Oxygen equipment (E1390, E0431)',
        authNumber: 'PA-2025-00841',
        expiresOn: '2025-12-31',
        status: 'Renew soon',
      },
      {
        id: 'pa-1002',
        description: 'Home health aide — 3×/week',
        authNumber: 'PA-2025-01002',
        expiresOn: '2026-06-30',
        status: 'Active',
      },
    ],
  },
  {
    id: 'pat-0002',
    patientId: 'PT-00187',
    firstName: 'Ruth',
    lastName: 'Thompson',
    dateOfBirth: '1952-07-22',
    primaryPayor: 'BlueCross BlueShield TX',
    memberId: 'BCB204918374',
    lastVerified: '2026-05-01',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 142.5,
  },
  {
    id: 'pat-0003',
    patientId: 'PT-00344',
    firstName: 'Anil',
    lastName: 'Patel',
    dateOfBirth: '1945-11-05',
    primaryPayor: 'BlueCross BlueShield TX',
    memberId: 'BCB118204736',
    lastVerified: '2026-04-14',
    eligibility: 'expires_soon',
    status: 'active',
    openArAmount: 318.74,
  },
  {
    id: 'pat-0004',
    patientId: 'PT-00412',
    firstName: 'Diana',
    lastName: 'Liu',
    dateOfBirth: '1978-09-30',
    primaryPayor: 'Aetna Texas',
    memberId: 'AET502938471',
    lastVerified: '2026-05-20',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 0,
  },
  {
    id: 'pat-0005',
    patientId: 'PT-00503',
    firstName: 'Marcus',
    lastName: 'Webb',
    dateOfBirth: '1961-02-18',
    primaryPayor: 'UnitedHealthcare',
    memberId: 'UHC674920183',
    lastVerified: '2026-03-10',
    eligibility: 'lapsed',
    status: 'on_hold',
    openArAmount: 1245.6,
  },
  {
    id: 'pat-0006',
    patientId: 'PT-00228',
    firstName: 'Sara',
    lastName: 'Nguyen',
    dateOfBirth: '1959-05-07',
    primaryPayor: 'Cigna Health',
    memberId: 'CGN339021748',
    lastVerified: '2026-05-19',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 87.3,
  },
  {
    id: 'pat-0007',
    patientId: 'PT-00467',
    firstName: 'Robert',
    lastName: 'Chen',
    dateOfBirth: '1958-12-02',
    primaryPayor: 'Texas Medicaid (TMHP)',
    memberId: 'TMP180039726',
    lastVerified: '2026-05-15',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 0,
  },
  {
    id: 'pat-0008',
    patientId: 'PT-00591',
    firstName: 'Linda',
    lastName: 'Ramirez',
    dateOfBirth: '1972-04-12',
    primaryPayor: 'Humana',
    memberId: 'HUM558203914',
    lastVerified: '2026-05-08',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 421.05,
  },
  {
    id: 'pat-0009',
    patientId: 'PT-00608',
    firstName: 'Kevin',
    lastName: "O'Brien",
    dateOfBirth: '1985-08-23',
    primaryPayor: 'Molina Healthcare',
    memberId: 'MOL301847265',
    lastVerified: '2026-04-22',
    eligibility: 'expires_soon',
    status: 'active',
    openArAmount: 215.0,
  },
  {
    id: 'pat-0010',
    patientId: 'PT-00133',
    firstName: 'Amelia',
    lastName: 'Johnson',
    dateOfBirth: '1949-01-29',
    primaryPayor: 'Medicare',
    memberId: 'MCR849201736',
    lastVerified: '2026-05-22',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 60.25,
  },
  {
    id: 'pat-0011',
    patientId: 'PT-00702',
    firstName: 'Daniel',
    lastName: 'Hughes',
    dateOfBirth: '1965-06-04',
    primaryPayor: 'WellCare',
    memberId: 'WLC192038475',
    lastVerified: '2026-02-26',
    eligibility: 'lapsed',
    status: 'on_hold',
    openArAmount: 980.12,
  },
  {
    id: 'pat-0012',
    patientId: 'PT-00256',
    firstName: 'Priya',
    lastName: 'Shah',
    dateOfBirth: '1990-10-11',
    primaryPayor: 'Aetna Texas',
    memberId: 'AET663109284',
    lastVerified: '2026-05-21',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 0,
  },
  {
    id: 'pat-0013',
    patientId: 'PT-00378',
    firstName: 'Carlos',
    lastName: 'Reyes',
    dateOfBirth: '1955-03-19',
    primaryPayor: 'UnitedHealthcare',
    memberId: 'UHC557390218',
    lastVerified: '2026-05-23',
    eligibility: 'pending',
    status: 'pending',
    openArAmount: 0,
  },
  {
    id: 'pat-0014',
    patientId: 'PT-00489',
    firstName: 'Emily',
    lastName: 'Carter',
    dateOfBirth: '1981-07-15',
    primaryPayor: 'BlueCross BlueShield TX',
    memberId: 'BCB446728103',
    lastVerified: '2026-05-12',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 312.4,
  },
  {
    id: 'pat-0015',
    patientId: 'PT-00640',
    firstName: 'Hiroshi',
    lastName: 'Tanaka',
    dateOfBirth: '1947-09-08',
    primaryPayor: 'Medicare',
    memberId: 'MCR337829405',
    lastVerified: '2026-04-30',
    eligibility: 'expires_soon',
    status: 'active',
    openArAmount: 178.9,
  },
  {
    id: 'pat-0016',
    patientId: 'PT-00715',
    firstName: 'Olivia',
    lastName: 'Brooks',
    dateOfBirth: '1996-12-18',
    primaryPayor: 'Cigna Health',
    memberId: 'CGN780192345',
    lastVerified: '2026-05-09',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 0,
  },
  {
    id: 'pat-0017',
    patientId: 'PT-00822',
    firstName: 'Maya',
    lastName: 'Singh',
    dateOfBirth: '1963-02-27',
    primaryPayor: 'Texas Medicaid (TMHP)',
    memberId: 'TMP228371490',
    lastVerified: '2026-01-30',
    eligibility: 'lapsed',
    status: 'inactive',
    openArAmount: 745.5,
  },
  {
    id: 'pat-0018',
    patientId: 'PT-00903',
    firstName: 'Henry',
    lastName: 'Anderson',
    dateOfBirth: '1976-11-23',
    primaryPayor: 'Humana',
    memberId: 'HUM911283746',
    lastVerified: '2026-05-25',
    eligibility: 'verified',
    status: 'active',
    openArAmount: 0,
  },
]
